import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { FiSearch } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  removeTemplate,
  setPrebuiltCategoryCounts,
  setCurrentTemplateId,
} from '../../features/templates/templatesSlice';
import { BaseTemplate } from '../../features/templates/templatesSlice';
import { PrebuiltTemplate } from '../../features/templates/types';
import CreateTemplateModal from './CreateTemplateModal';
import { RootState, AppDispatch } from '../../app/store';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

const FiSearchComponent = FiSearch as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

type GalleryTemplate = BaseTemplate | PrebuiltTemplate;

const UNCATEGORIZED = 'Uncategorized';

interface TemplatesGalleryProps {
  templates: GalleryTemplate[];
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  fromTab2: string | undefined;
  onSearch: (searchTerm: string) => void;
  searchTerm: string;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

// ⭐ Single source of truth — reads from metadata.category first (matching the API object)
const getTemplateCategory = (template: any): string => {
  const raw = template?.metadata?.category ?? template?.category;
  const c = typeof raw === 'string' ? raw.trim() : '';
  return c ? c : UNCATEGORIZED;
};

const getStoredPageState = () => {
  const stored = localStorage.getItem('galleryPageState');
  return stored
    ? JSON.parse(stored)
    : { searchQuery: '', selectedCategories: [] };
};

const storePageState = (state: {
  searchQuery: string;
  selectedCategories: string[];
}) => {
  localStorage.setItem('galleryPageState', JSON.stringify(state));
};

// Pretty-print category ("marketing" → "Marketing")
const prettyCategory = (cat: string): string =>
  cat === UNCATEGORIZED ? cat : cat.charAt(0).toUpperCase() + cat.slice(1);

const TemplatesGallery: React.FC<TemplatesGalleryProps> = ({
  templates,
  onSelect,
  onEdit,
  fromTab2,
  onSearch,
  searchTerm,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
}) => {
  const storedState = getStoredPageState();
  const [searchQuery, setSearchQuery] = useState(
    searchTerm || storedState.searchQuery
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    storedState.selectedCategories
  );
  const [showModal, setShowModal] = useState(false);

  // ⭐ Tooltip state (portal-rendered)
  const [tooltip, setTooltip] = useState<{
    text: string;
    top: number;
    left: number;
  } | null>(null);

  // ⭐ All prebuilt templates fetched with size=1000 — used for client-side filtering
  const [allPrebuiltTemplates, setAllPrebuiltTemplates] = useState<
    PrebuiltTemplate[]
  >([]);
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);

  // ⭐ Client-side page for category-filtered view
  const [localPage, setLocalPage] = useState<number>(1);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const prebuiltCategoryCounts = useSelector(
    (state: RootState) => state.templates.prebuiltCategoryCounts
  );
  const prebuiltStatus = useSelector(
    (state: RootState) => state.templates.prebuiltStatus
  );

  // ====================================================================
  // Fetch ALL prebuilt templates (size=1000) — build categories + counts,
  // and keep the full list for client-side category filtering.
  // ====================================================================
  const fetchAllCategories = async () => {
    try {
      setIsCategoriesLoading(true);
      const { token, tenantId, userId } = getAuthContext();
      if (!token) return;

      let url = `${BASE_URL}/v1/templates/pre-build?orgId=org_123456&page=0&size=1000`;
      if (searchTerm && searchTerm.trim()) {
        url += `&name=${encodeURIComponent(searchTerm.trim())}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId || '',
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // console.error('Failed to fetch all prebuilt templates:', response.status);
        toast.error('Failed to fetch templates. Please try again later.');
        setAllPrebuiltTemplates([]);
        setAllCategories([]);
        dispatch(setPrebuiltCategoryCounts({}));
        return;
      }

      const data = await response.json();
      const content: any[] = data.content || [];

      const active = content.filter((t) => t.status !== 'ARCHIVED');

      // ⭐ Uses getTemplateCategory → reads metadata.category
      const counts: Record<string, number> = {};
      const set = new Set<string>();

      active.forEach((t) => {
        const cat = getTemplateCategory(t);
        set.add(cat);
        counts[cat] = (counts[cat] || 0) + 1;
      });

      const sortedCategories = Array.from(set).sort((a, b) => {
        if (a === UNCATEGORIZED) return 1;
        if (b === UNCATEGORIZED) return -1;
        return a.localeCompare(b, undefined, { sensitivity: 'base' });
      });

      setAllPrebuiltTemplates(active as PrebuiltTemplate[]);
      setAllCategories(sortedCategories);
      dispatch(setPrebuiltCategoryCounts(counts));
    } catch (err) {
      // console.error('Error fetching all prebuilt templates:', err);
      toast.error('Error fetching templates. Please try again later.');
      setAllPrebuiltTemplates([]);
      setAllCategories([]);
      dispatch(setPrebuiltCategoryCounts({}));
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Persist UI state
  useEffect(() => {
    storePageState({ searchQuery, selectedCategories });
  }, [searchQuery, selectedCategories]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== searchTerm) {
        onSearch(searchQuery);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, onSearch, searchTerm]);

  useEffect(() => {
    if (searchTerm !== searchQuery) {
      setSearchQuery(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // ⭐ Auto-hide tooltip on scroll/resize so it doesn't float away
  useEffect(() => {
    if (!tooltip) return;
    const hide = () => setTooltip(null);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [tooltip]);

  // ====================================================================
  // Display mode
  // ====================================================================
  const isCategoryFilterActive = selectedCategories.length > 0;
  const ITEMS_PER_PAGE = pageSize || 9;

  // Prebuilt-only from parent (used when no category selected)
  const prebuiltTemplates = useMemo(
    () => templates.filter((t): t is PrebuiltTemplate => t.isPrebuilt === true),
    [templates]
  );

  // ⭐ Client-side filter from the size=1000 list (SelectTemplate-style)
  const filteredByCategory = useMemo(() => {
    if (!isCategoryFilterActive) return [];
    return allPrebuiltTemplates
      .filter((t) => selectedCategories.includes(getTemplateCategory(t)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allPrebuiltTemplates, selectedCategories, isCategoryFilterActive]);

  // Reset local page when categories change
  useEffect(() => {
    setLocalPage(1);
  }, [selectedCategories]);

  // Client-side pagination for the filtered view
  const paginatedFiltered = useMemo(() => {
    const startIndex = (localPage - 1) * ITEMS_PER_PAGE;
    return filteredByCategory.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredByCategory, localPage, ITEMS_PER_PAGE]);

  const displayTotalPages = isCategoryFilterActive
    ? Math.ceil(filteredByCategory.length / ITEMS_PER_PAGE)
    : totalPages;

  const displayCurrentPage = isCategoryFilterActive ? localPage : currentPage + 1;

  const displayTotalCount = isCategoryFilterActive
    ? filteredByCategory.length
    : totalCount;

  const displayedTemplates = isCategoryFilterActive
    ? paginatedFiltered
    : prebuiltTemplates;

  const startItem = isCategoryFilterActive
    ? filteredByCategory.length === 0
      ? 0
      : (localPage - 1) * ITEMS_PER_PAGE + 1
    : totalCount === 0
      ? 0
      : currentPage * pageSize + 1;

  const endItem = isCategoryFilterActive
    ? Math.min(localPage * ITEMS_PER_PAGE, filteredByCategory.length)
    : Math.min((currentPage + 1) * pageSize, totalCount);

  const handleCategoryChange = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setSelectedCategories([]);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSelectedCategories([]);
  };

  // Server-side pagination (no category filter) — calls parent API
  const handlePageChange = (page: number) => {
    onPageChange(page - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Client-side pagination (category filter active)
  const handleLocalPageChange = (page: number) => {
    if (page >= 1 && page <= displayTotalPages) {
      setLocalPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDelete = (id: string) => {
    dispatch(removeTemplate(id));
    toast.success('Template deleted successfully!', {
      position: 'top-right',
      autoClose: 3000,
    });
  };

  const isUserTemplate = (t: GalleryTemplate): t is BaseTemplate => {
    return !t.isPrebuilt;
  };

  const handleEditClick = (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    storePageState({ searchQuery, selectedCategories });
    localStorage.setItem('currentTemplateId', templateId);
    dispatch(setCurrentTemplateId(templateId));
    navigate('/templates/editor', { state: { fromTab2 } });
  };

  // ⭐ Tooltip handlers
  const handleNameMouseEnter = (
    e: React.MouseEvent<HTMLHeadingElement>,
    text: string
  ) => {
    const el = e.currentTarget;
    // Only show tooltip when the text is actually truncated
    if (el.scrollWidth <= el.clientWidth) return;

    const rect = el.getBoundingClientRect();
    setTooltip({
      text,
      top: rect.top + window.scrollY - 8,
      left: rect.left + window.scrollX + rect.width / 2,
    });
  };

  const handleNameMouseLeave = () => setTooltip(null);

  useEffect(() => {
    const scrollToTemplateId = localStorage.getItem('scrollToTemplateId');
    if (scrollToTemplateId) {
      const timer = setTimeout(() => {
        const element = document.getElementById(`template-${scrollToTemplateId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('ring-2', 'ring-orange-400', 'ring-opacity-50');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-orange-400', 'ring-opacity-50');
          }, 1000);
        }
        localStorage.removeItem('scrollToTemplateId');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  const totalVisibleCount = allPrebuiltTemplates.length || prebuiltTemplates.length;

  if (prebuiltStatus === 'loading' && !isCategoryFilterActive) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="flex gap-6">
      {/* Sidebar */}
      <div
        className="w-1/5 bg-white rounded-md p-5 border border-gray-200 shadow-sm sticky top-6"
        style={{ maxHeight: 'calc(100vh - 100px)', height: 'fit-content' }}
      >
        <button
          className="w-full text-left font-medium mb-4 px-3 py-2 rounded flex justify-between items-center"
          style={{ backgroundColor: '#E0F8FB', color: '#00899E' }}
          onClick={() => {
            setSelectedCategories([]);
            setLocalPage(1);
            onPageChange(0);
          }}
        >
          All Templates
          <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded">
            {totalVisibleCount}
          </span>
        </button>

        <div className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto pr-1 -mr-1">
          <style>{`
            div::-webkit-scrollbar { width: 6px; }
            div::-webkit-scrollbar-track { background: transparent; }
            div::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 3px; }
            div::-webkit-scrollbar-thumb:hover { background: #a0aec0; }
          `}</style>

          {isCategoriesLoading && allCategories.length === 0 && (
            <div className="text-xs text-gray-400 px-2 py-1">Loading categories…</div>
          )}

          {!isCategoriesLoading && allCategories.length === 0 && (
            <div className="text-xs text-gray-400 px-2 py-1">No categories</div>
          )}

          {allCategories.map((cat) => {
            const count = prebuiltCategoryCounts[cat] || 0;
            return (
              <label
                key={cat}
                className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(cat)}
                  onChange={() => handleCategoryChange(cat)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 flex-1">
                  {prettyCategory(cat)}
                </span>
                <span className="text-xs text-gray-500">({count})</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-6 gap-4">
          <div className="flex gap-3 flex-1 w-full">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full pl-4 pr-14 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-9 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none z-10"
                  aria-label="Clear search"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
              <FiSearchComponent
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                size={20}
              />
            </div>
          </div>

          <button
            onClick={() => setShowModal(true)}
            className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg text-sm transition"
          >
            + Create Template
          </button>
        </div>

        {displayedTemplates.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedTemplates.map((template) => (
                <div
                  key={template.id}
                  id={`template-${template.id}`}
                  className="group relative bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
                    {template.thumbnail ? (
                      <img
                        src={template.thumbnail}
                        alt={template.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-400 text-sm">Template</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="flex gap-3 transform translate-y-4 group-hover:translate-y-0 transition-transform">
                        <button
                          onClick={(e) => handleEditClick(e, template.id)}
                          className="px-4 py-2 bg-white text-gray-800 rounded-lg font-medium text-sm hover:bg-gray-100 shadow"
                        >
                          Edit
                        </button>
                        {isUserTemplate(template) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(template.id);
                            }}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium text-sm hover:bg-red-700 shadow"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    {/* ⭐ Tooltip on truncated name */}
                    <h3
                      className="font-semibold text-gray-800 truncate"
                      onMouseEnter={(e) => handleNameMouseEnter(e, template.name)}
                      onMouseLeave={handleNameMouseLeave}
                    >
                      {template.name}
                    </h3>
                    {/* ⭐ Uses metadata.category now */}
                    <p className="text-xs text-gray-500 mt-1">
                      {prettyCategory(getTemplateCategory(template))}
                    </p>
                  </div>
                  {(template.isPrebuilt || isCategoryFilterActive) && (
                    <div className="absolute top-3 left-3">
                      <span className="bg-blue-600 text-white text-xs px-2.5 py-1 rounded-full font-medium">
                        PREBUILT
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* ============== PAGINATION ============== */}
            {isCategoryFilterActive ? (
              // ⭐ SelectTemplate-style pagination (client-side)
              displayTotalPages > 1 && (
                <div className="flex items-center justify-between mt-8 text-sm text-gray-500">
                  <span>
                    Showing {startItem} to {endItem} of {displayTotalCount} templates
                  </span>

                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleLocalPageChange(displayCurrentPage - 1)}
                      disabled={displayCurrentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    {(() => {
                      const pages: (number | string)[] = [];
                      const total = displayTotalPages;
                      const current = displayCurrentPage;
                      const delta = 1;

                      pages.push(1);
                      let rangeStart = Math.max(2, current - delta);
                      let rangeEnd = Math.min(total - 1, current + delta);

                      if (rangeStart > 2) pages.push('...');
                      for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
                      if (rangeEnd < total - 1) pages.push('...');
                      if (total > 1) pages.push(total);

                      return pages.map((page, index) => {
                        if (page === '...') {
                          return (
                            <span
                              key={`ellipsis-${index}`}
                              className="px-3 py-2 text-sm text-gray-500"
                            >
                              …
                            </span>
                          );
                        }
                        const pageNumber = page as number;
                        const isActive = pageNumber === current;
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handleLocalPageChange(pageNumber)}
                            className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                              ? 'bg-orange-500 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      });
                    })()}

                    <button
                      onClick={() => handleLocalPageChange(displayCurrentPage + 1)}
                      disabled={displayCurrentPage === displayTotalPages}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )
            ) : (
              // Original centered pagination (server-side)
              <>
                {displayTotalPages > 1 && (
                  <div className="mt-10 flex items-center justify-center gap-2">
                    <button
                      onClick={() => handlePageChange(displayCurrentPage - 1)}
                      disabled={displayCurrentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    {(() => {
                      const pages = [];
                      const total = displayTotalPages;
                      const current = displayCurrentPage;
                      const delta = 1;

                      pages.push(1);
                      let rangeStart = Math.max(2, current - delta);
                      let rangeEnd = Math.min(total - 1, current + delta);

                      if (rangeStart > 2) pages.push('...');
                      for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
                      if (rangeEnd < total - 1) pages.push('...');
                      if (total > 1) pages.push(total);

                      return pages.map((page, index) => {
                        if (page === '...') {
                          return (
                            <span
                              key={`ellipsis-${index}`}
                              className="px-3 py-2 text-sm text-gray-500"
                            >
                              …
                            </span>
                          );
                        }
                        const pageNumber = page as number;
                        const isActive = pageNumber === current;
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                              ? 'bg-orange-500 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      });
                    })()}

                    <button
                      onClick={() => handlePageChange(displayCurrentPage + 1)}
                      disabled={displayCurrentPage === displayTotalPages}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}

                <div className="mt-4 text-center text-sm text-gray-600">
                  Showing {startItem} to {endItem} of {displayTotalCount} templates
                </div>
              </>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">No templates found</p>
            <p className="text-gray-400 mt-2">
              {searchQuery || selectedCategories.length > 0
                ? 'Try adjusting your search or filters'
                : 'No prebuilt templates available at the moment.'}
            </p>
          </div>
        )}
      </div>

      <CreateTemplateModal
        fromTab2={fromTab2}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />

      {/* ⭐ Portal tooltip — renders into document.body to escape overflow-hidden */}
      {tooltip &&
        ReactDOM.createPortal(
          <div
            role="tooltip"
            style={{
              position: 'absolute',
              top: tooltip.top,
              left: tooltip.left,
              transform: 'translate(-50%, -100%)',
              zIndex: 9999,
              pointerEvents: 'none',
            }}
            className="bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-md shadow-lg max-w-xs break-words whitespace-normal"
          >
            {tooltip.text}
            <span
              style={{
                position: 'absolute',
                bottom: -4,
                left: '50%',
                transform: 'translateX(-50%) rotate(45deg)',
                width: 8,
                height: 8,
                background: '#111827',
              }}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default TemplatesGallery;